import React, { useState } from 'react';
import { withMask } from 'use-mask-input';
import { Gift, User, Phone, Sparkles } from 'lucide-react';
import { WheelData, SpinRequest } from '../../../../types';
import DynamicFormFields from '../../../../components/DynamicFormFields';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import TermsModal from './TermsModal';
import ProductsShowcase from './ProductsShowcase';

interface SpinFormProps {
  wheelData: WheelData;
  formData: SpinRequest;
  customFieldsData: Record<string, any>;
  customFieldsErrors: Record<string, string>;
  acceptedTerms: boolean;
  isFormSubmitting: boolean;
  theme: {
    background_color: string;
    text_color: string;
    font_family: string;
    logo_url?: string;
  };
  onFormChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCustomFieldChange: (fieldId: string, value: any) => void;
  onTermsChange: (accepted: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const SpinForm: React.FC<SpinFormProps> = ({
  wheelData,
  formData,
  customFieldsData,
  customFieldsErrors,
  acceptedTerms,
  isFormSubmitting,
  theme,
  onFormChange,
  onCustomFieldChange,
  onTermsChange,
  onSubmit
}) => {
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const handleTermsLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (wheelData.wheel.terms_type === 'external' && wheelData.wheel.terms_content) {
      window.open(wheelData.wheel.terms_content, '_blank', 'noopener,noreferrer');
    } else if (wheelData.wheel.terms_type === 'internal') {
      setIsTermsModalOpen(true);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4"
      style={{
        background: `linear-gradient(to bottom right, ${theme.background_color}, ${theme.background_color}dd)`,
        color: theme.text_color,
        fontFamily: theme.font_family
      }}
    >
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          {theme.logo_url ? (
            <div className="mx-auto mb-4 flex justify-center">
              <img
                src={theme.logo_url}
                alt="Logo"
                className="h-16 max-w-full object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="mx-auto h-16 w-16 bg-white rounded-full flex items-center justify-center mb-4">
              <Gift className="h-8 w-8" style={{ color: theme.background_color }} />
            </div>
          )}
          <h1 className="text-3xl font-bold mb-2">
            {wheelData.wheel.name}
          </h1>
          <p style={{ opacity: 0.9 }}>
            Preencha seus dados para participar do sorteio
          </p>
        </div>

        {/* Showcase de produtos */}
        <ProductsShowcase wheelData={wheelData} theme={theme} />

        <form onSubmit={onSubmit} className="bg-white rounded-lg p-6 shadow-xl">
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                <User className="inline h-4 w-4 mr-1" />
                Nome completo
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={onFormChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900"
                placeholder="Seu nome"
                required
              />
            </div>

            {!wheelData.wheel.simplified_form && (
              <>
                <div>
                  <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="inline h-4 w-4 mr-1" />
                    WhatsApp
                  </label>
                  <input
                    type="tel"
                    id="whatsapp"
                    name="whatsapp"
                    ref={withMask('(99) 99999-9999')}
                    value={formData.whatsapp}
                    onChange={onFormChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900"
                    placeholder="(00) 00000-0000"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="birthdate" className="block text-sm font-medium text-gray-700 mb-1">
                    Data de nascimento
                  </label>
                  <input
                    type="tel"
                    id="birthdate"
                    name="birthdate"
                    ref={withMask('99/99/9999')}
                    value={formData.birthdate}
                    onChange={onFormChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900"
                    placeholder="DD/MM/AAAA"
                  />
                </div>
              </>
            )}

            {wheelData?.wheel.custom_fields && wheelData.wheel.custom_fields.length > 0 && (
              <DynamicFormFields
                fields={wheelData.wheel.custom_fields}
                values={customFieldsData}
                errors={customFieldsErrors}
                onChange={onCustomFieldChange}
              />
            )}

            <div className="flex items-start">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => onTermsChange(e.target.checked)}
                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="terms" className="ml-2 text-sm text-gray-700">
                Aceito os{' '}
                {wheelData.wheel.terms_type && wheelData.wheel.terms_content ? (
                  <>
                    <button
                      type="button"
                      onClick={handleTermsLinkClick}
                      className="text-primary-600 hover:text-primary-700 underline"
                    >
                      termos e regulamento
                    </button>
                    {' '}e concordo em receber comunicações
                  </>
                ) : (
                  'termos de uso e concordo em receber comunicações'
                )}
              </label>
            </div>

            <button
              type="submit"
              disabled={!acceptedTerms || isFormSubmitting}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isFormSubmitting ? <LoadingSpinner size='sm' className='mr-2' /> : <Sparkles className="h-5 w-5 mr-2" />}
              Começar
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs" style={{ opacity: 0.8 }}>
            {wheelData.stats.available_products} prêmios disponíveis •{' '}
            {wheelData.wheel.total_spins} participações realizadas
          </p>
        </div>
      </div>

      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        termsType={wheelData.wheel.terms_type}
        termsContent={wheelData.wheel.terms_content}
        wheelName={wheelData.wheel.name}
      />
    </div>
  );
};

export default SpinForm;
